// PageHeaderAvatar.tsx
import React, { useRef, useState } from 'react'
import { Badge, ListItemIcon, ListItemText, Menu, Tooltip } from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import { AccountCircle, KeyboardArrowDown, Settings } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import navigation from '../../config/navigation'
import { StyledBadgeButton } from '../../styles/ProfileViewStyles'
import PageHeaderAvatarDialog from './PageHeaderAvatarDialog'
import AvatarComponent from '../avatar/AvatarComponent'
import { User } from '../../store/types'
import { useSelector } from 'react-redux'
import { getIsBaseUser } from '../../store/redux/reducers/currentUserSlice'

type PageHeaderAvatarProps = {
  profileIcon: string | undefined
  profileExtension: string | undefined
  displayTooltip: boolean
  currentUser: User | null
}

const PageHeaderAvatar = ({ profileIcon, profileExtension, displayTooltip, currentUser }: PageHeaderAvatarProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const size = 35
  const scaledSize = size / 3
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const badgeButtonRef = useRef<HTMLButtonElement | null>(null)
  const [openDialog, setOpenDialog] = useState(false)

  const handleClick = () => {
    if (badgeButtonRef.current) {
      setAnchorEl(badgeButtonRef.current)
    }
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSettingsClick = () => {
    setAnchorEl(null)
    setOpenDialog(true)
  }

  const handleProfileClick = () => {
    setAnchorEl(null)
    navigate(navigation.myProfile)
  }

  const isBaseUser = useSelector(getIsBaseUser)

  return (
    <>
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ '.MuiBadge-badge': { p: 0 } }}
        badgeContent={
          <>
            <StyledBadgeButton
              ref={badgeButtonRef}
              sx={{ width: scaledSize, height: scaledSize }}
              onClick={handleClick}
              aria-label={t('aria.pageHeader.avatarBadgeButton')}
            >
              <KeyboardArrowDown sx={{ width: scaledSize }} />
            </StyledBadgeButton>
            <Menu
              disableAutoFocusItem
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={handleProfileClick} sx={{ pr: { xs: 5, sm: 8, md: 10 } }}>
                <ListItemIcon>
                  <AccountCircle />
                </ListItemIcon>
                <ListItemText>{t('page.name.myProfile')}</ListItemText>
              </MenuItem>
              {!isBaseUser && (
                <MenuItem onClick={handleSettingsClick}>
                  <ListItemIcon>
                    <Settings />
                  </ListItemIcon>
                  <ListItemText>{t('page.name.settings')}</ListItemText>
                </MenuItem>
              )}
            </Menu>
          </>
        }
      >
        <Tooltip title={displayTooltip ? `${currentUser?.firstName} ${currentUser?.lastName}` : ''} placement="left">
          <AvatarComponent
            alt={`${currentUser?.firstName} ${currentUser?.lastName}`}
            avatar={profileIcon}
            size={size}
            extension={profileExtension}
          />
        </Tooltip>
      </Badge>
      <PageHeaderAvatarDialog open={openDialog} setOpen={setOpenDialog} />
    </>
  )
}

export default PageHeaderAvatar